import { useQuery } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'

export interface HeartRateHistogramBucket {
  bpm: number
  count: number
  percentage: number
  timeInMinutes: number
}

export const useHeartRateHistogram = (workoutId?: string, minHR = 100, maxHR = 180) => {
  return useQuery<HeartRateHistogramBucket[]>({
    queryKey: ['heart-rate-histogram', workoutId, minHR, maxHR],
    enabled: Boolean(workoutId),
    queryFn: async () => {
      const { data: histogramData, error: histogramError } = await supabase.rpc('get_heart_rate_histogram', {
        p_workout_id: workoutId!,
      })

      if (histogramError) throw histogramError

      if (!histogramData || histogramData.length === 0) {
        return []
      }

      const totalSamples = histogramData.reduce((sum: number, row: any) => sum + row.sample_count, 0)
      const bucketMap = new Map<number, { count: number; timeSeconds: number }>()

      histogramData.forEach((row: any) => {
        bucketMap.set(row.bpm, {
          count: row.sample_count,
          timeSeconds: row.time_seconds,
        })
      })

      const allBuckets: HeartRateHistogramBucket[] = []
      for (let bpm = minHR; bpm <= maxHR; bpm++) {
        const bucketData = bucketMap.get(bpm)
        const count = bucketData?.count || 0
        const timeInMinutes = bucketData ? bucketData.timeSeconds / 60 : 0
        allBuckets.push({
          bpm,
          count,
          percentage: totalSamples > 0 ? (count / totalSamples) * 100 : 0,
          timeInMinutes,
        })
      }

      return allBuckets
    },
  })
}
